/** Opening a Job from a portal page in the existing client Job viewer, over the page, without navigating. */
import { HttpErrorResponse } from '@angular/common/http';
import { ActivityEntityName, ClientJob, PortalClientSecurityRight } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService, WorkBuddyDialogService } from '@workbuddy/ui/services';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';

/** The services the opening needs, injected by the caller (a cell or a field renderer). */
export interface PortalJobDialogDeps {
  data: EntityDataServiceRaw;
  dialogs: WorkBuddyDialogService;
  toast: ToastService;
}

/** Whether this portal user may open Jobs at all; the server rechecks the right and the Job's customer. */
export function canOpenPortalJobs(rights: RightsService): boolean {

  return rights.hasRights(PortalClientSecurityRight.CanAccessJobs) || rights.hasRights(PortalClientSecurityRight.ViewJobsInSite);
}

/**
 * Reads the authorised Job, then opens the client Job viewer. `isCurrent` is asked after the read: a caller whose
 * customer or Job changed meanwhile gets nothing, not a stale dialog. A refused or failed read is a toast.
 */
export async function openPortalJobDialog(deps: PortalJobDialogDeps, jobId: string, isCurrent: () => boolean): Promise<DynamicDialogRef | undefined> {

  try {
    const [response, viewer] = await Promise.all([
      firstValueFrom(deps.data.getById<ClientJob>(ActivityEntityName.Job, jobId)),
      import('@workbuddy/ui/modules/client.manage.job/src/components/client.manage.job/client.manage.job.component'),
    ]);
    if (!isCurrent()) return undefined;
    if (!response.item) throw new Error('Job not found.');
    return deps.dialogs.open(viewer.ClientManageJobComponent, { data: { job: response.item } });
  } catch (error) {
    if (!isCurrent()) return undefined;
    const message = error instanceof HttpErrorResponse && error.status === 403
      ? 'You do not have permission to view this Job.'
      : 'Unable to load this Job. It may no longer be available.';
    deps.toast.error('Job', message);
    return undefined;
  }
}
